/* eslint-disable no-await-in-loop */
import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Refresh } from '@mui/icons-material';
import { Box, Typography } from '../../../../1-primative';
import { Alert, Button, CircularProgress } from '../../../../2-component';
import { WorkflowContext } from '../../workflowCompletion';

const SEND_INTEGRATION_FORM_PACK = gql`
  mutation sendIntegrationFormPack($input: SendIntegrationFormPackInput!) {
    sendIntegrationFormPack(input: $input) {
      integrationFormPack {
        id
      }
    }
  }
`;

export const SendIntegrationFormPack = ({
  onNext, workflowCompletion,
}: {
  onNext: () => void;
  workflowCompletion: any;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { workflowData } = useContext(WorkflowContext);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [sendIntegrationFormPack] = useMutation(SEND_INTEGRATION_FORM_PACK, {
    onCompleted: () => {
      setError(null);
      onNext();
    },
  });

  const attemptSendIntegrationFormPack = useCallback(async () => {
    if (!workflowCompletion.organization?.id || !workflowData.integrationFormPackId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const variables = {
      input: {
        integrationFormPackId: workflowData.integrationFormPackId,
      },
      skipErrorHandler: true,
    };

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await sendIntegrationFormPack({ variables });
        return;
      } catch (err) {
        if (attempt === 3) {
          setError(err as Error);
        }
      }
    }
    setIsLoading(false);
  }, [workflowCompletion, workflowData, sendIntegrationFormPack]);

  useEffect(() => {
    attemptSendIntegrationFormPack();
  }, [attemptSendIntegrationFormPack]);

  return (
    <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' gap={1} minHeight='300px'>
      <Typography variant='headingMedium'>{t('workflowCompletions:sendIntegrationFormPack.title')}</Typography>
      <Typography variant='bodyMedium' sx={{ mb: 4 }}>{t('workflowCompletions:sendIntegrationFormPack.description')}</Typography>
      {error && (
        <Box mb={2}>
          <Alert severity='error'>
            {error.message}
          </Alert>
          <Box mt={2} display='flex' justifyContent='center'>
            <Button onClick={attemptSendIntegrationFormPack} disabled={isLoading} label={t('shared:retry')} variant='text' leadingIcon={Refresh} />
          </Box>
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
